<template>
    <div class="section pricing-section fade-in-section">
        <h2 class="section-title">Choose Your Plan</h2>
        <div class="pricing-grid">
            <div
                v-for="(tier, index) in pricingTiers"
                :key="index"
                class="pricing-card"
                :class="[tier.tierClass, { featured: tier.featured }]"
            >
                <div v-if="tier.featured" class="featured-badge">Most Popular</div>
                <h3>{{ tier.name }}</h3>
                <div class="price">
                    <span class="currency">$</span>
                    <span class="amount">{{ tier.price }}</span>
                    <span class="period">{{ tier.period }}</span>
                </div>
                <p class="tier-description">{{ tier.description }}</p>
                <ul class="features-list">
                    <li v-for="(feature, idx) in tier.features" :key="idx">
                        <span class="check">✓</span> {{ feature }}
                    </li>
                </ul>
                <router-link
                    :to="tier.link"
                    class="btn"
                    :class="'btn-' + tier.tierClass"
                >
                    {{ tier.cta }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PricingSection',
    data() {
        return {
            pricingTiers: [
                {
                    name: 'FREE',
                    price: '0',
                    period: '',
                    description: 'See what Curie can do',
                    features: [
                        '5 text questions/day',
                    ],
                    cta: 'Get Started',
                    link: '/register',
                    featured: false,
                    tierClass: 'tier-free'
                },
                {
                    name: 'STUDENT',
                    price: '9.99',
                    period: '/month',
                    description: 'Or $99.90/year',
                    features: [
                        'Unlimited questions',
                        'All question types',
                        'Basic analytics',
                    ],
                    cta: 'Start Free Trial',
                    link: '/register',
                    featured: true,
                    tierClass: 'tier-1'
                },
                {
                    name: 'PRO',
                    price: '13.99',
                    period: '/month',
                    description: 'Or $139.90/year',
                    features: [
                        'Question hints',
                        'Question filtering',
                        'Advanced analytics',
                    ],
                    cta: 'Go Pro',
                    link: '/register',
                    featured: false,
                    tierClass: 'tier-2'
                }
            ]
        };
    }
};
</script>

<style scoped>
.section {
    position: relative;
    z-index: 2;
    padding: 80px 20px;
    max-width: 80vw;
    margin: 0 auto;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-bottom: 1px solid var(--accent);
}

.section-title {
    font-size: 48px;
    text-align: center;
    margin-bottom: 60px;
    color: var(--text-primary);
}

.pricing-section {
    background: transparent;
}

.pricing-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 25px;
    max-width: 1000px;
    margin: 0 auto;
}

@media (max-width: 900px) {
    .pricing-grid {
        grid-template-columns: 1fr;
        max-width: 400px;
    }
}

.pricing-card {
    background: transparent;
    padding: 40px 30px;
    border-radius: 12px;
    border: 1px solid rgba(150, 150, 150, 0.3);
    transition: all 0.3s;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 5;
}

/* Opaque base layer */
.pricing-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--bg-primary);
    border-radius: 12px;
    z-index: -2;
}

/* Tint layer */
.pricing-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(26, 24, 37, 0.95);
    border-radius: 12px;
    z-index: -1;
}

.pricing-card.featured {
    transform: scale(1.05);
}

.pricing-card:hover {
    transform: translateY(-5px);
}

.pricing-card.featured:hover {
    transform: scale(1.05) translateY(-5px);
}

/* FREE tier - Grey */
.pricing-card.tier-free {
    border-color: rgba(150, 150, 150, 0.6);
}

.pricing-card.tier-free::after {
    background: rgba(150, 150, 150, 0.15);
}

.pricing-card.tier-free:hover {
    border-color: rgba(150, 150, 150, 0.9);
    box-shadow: 0 8px 20px rgba(150, 150, 150, 0.2);
}

.pricing-card.tier-free h3,
.pricing-card.tier-free .amount {
    color: #aaa;
}

.pricing-card.tier-free .check {
    color: #aaa;
}

/* TIER 1 - Blue */
.pricing-card.tier-1 {
    border-color: rgba(100, 200, 255, 0.6);
}

.pricing-card.tier-1::after {
    background: rgba(100, 200, 255, 0.15);
}

.pricing-card.tier-1:hover {
    border-color: #64c8ff;
    box-shadow: 0 8px 20px rgba(100, 200, 255, 0.9);
}

.pricing-card.tier-1 h3,
.pricing-card.tier-1 .amount {
    color: #64c8ff;
}

.pricing-card.tier-1 .check {
    color: #64c8ff;
}

.pricing-card.tier-1.featured {
    border-width: 2px;
    border-color: #64c8ff;
}

.pricing-card.tier-1 .featured-badge {
    background: #64c8ff;
    color: #1a1a1a;
}

/* TIER 2 - Purple */
.pricing-card.tier-2 {
    border-color: rgba(147, 51, 234, 0.6);
}

.pricing-card.tier-2::after {
    background: rgba(147, 51, 234, 0.15);
}

.pricing-card.tier-2.featured {
    border-width: 2px;
    border-color: #9333ea;
}

.pricing-card.tier-2:hover {
    border-color: #9333ea;
    box-shadow: 0 8px 20px rgba(147, 51, 234, 0.9);
}

.pricing-card.tier-2 h3,
.pricing-card.tier-2 .amount {
    color: #9333ea;
}

.pricing-card.tier-2 .check {
    color: #9333ea;
}

.pricing-card.tier-2 .featured-badge {
    background: #9333ea;
}

/* TIER 3 - Gold/Accent */
.pricing-card.tier-3 {
    border-color: rgba(var(--accent-rgb), 0.3);
}

.pricing-card.tier-3::after {
    background: rgba(var(--accent-rgb), 0.15);
}

.pricing-card.tier-3:hover {
    border-color: var(--accent);
    box-shadow: 0 8px 20px rgba(var(--accent-rgb), 0.3);
}

.pricing-card.tier-3 h3,
.pricing-card.tier-3 .amount {
    color: var(--accent);
}

.pricing-card.tier-3 .check {
    color: var(--accent);
}

.featured-badge {
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--accent);
    color: var(--bg-primary);
    padding: 5px 20px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
}

.pricing-card h3 {
    font-size: 28px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    text-align: center;
}

.price {
    text-align: center;
    margin-bottom: 15px;
}

.currency {
    font-size: 24px;
    color: var(--text-primary);
    vertical-align: super;
}

.amount {
    font-size: 56px;
    font-weight: 700;
    color: var(--text-secondary);
}

.period {
    font-size: 18px;
    color: var(--text-primary);
    opacity: 0.7;
}

.tier-description {
    text-align: center;
    color: var(--text-primary);
    opacity: 0.8;
    margin-bottom: 30px;
    font-size: 16px;
}

.features-list {
    list-style: none;
    padding: 0;
    margin: 0 0 30px 0;
    flex-grow: 1;
}

.features-list li {
    color: var(--text-primary);
    padding: 12px 0;
    border-bottom: 1px solid var(--accent);
    font-size: 15px;
}

.features-list li:last-child {
    border-bottom: none;
}

.check {
    color: var(--text-secondary);
    font-weight: 700;
    margin-right: 10px;
}

.pricing-card .btn {
    width: 100%;
    text-align: center;
    margin-top: auto;
}

.btn {
    padding: 15px 40px;
    text-decoration: none;
    font-weight: 600;
    font-size: 16px;
    transition: transform 0.2s, box-shadow 0.2s;
    display: inline-block;
    border-radius: 8px;
}

/* Tier-specific buttons */
.btn-tier-free {
    background: rgba(150, 150, 150, 0.8);
    color: #1a1a1a;
}

.btn-tier-free:hover {
    background: #aaa;
}

.btn-tier-1 {
    background: #64c8ff;
    color: #1a1a1a;
}

.btn-tier-1:hover {
    background: #8ad4ff;
}

.btn-tier-2 {
    background: #9333ea;
    color: #fff;
}

.btn-tier-2:hover {
    background: #a855f7;
}

.btn-tier-3 {
    background: var(--accent);
    color: var(--bg-primary);
}

.btn-tier-3:hover {
    background: var(--text-primary);
}

.btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
}

.fade-in-section {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-section.is-visible {
    opacity: 1;
    transform: translateY(0);
}

@media (max-width: 768px) {
    .section-title {
        font-size: 36px;
    }
    
    .section {
        min-height: auto;
        padding: 60px 20px;
    }
}
</style>